<script setup lang="ts">
import { computed } from 'vue';
import type { FormField } from './types/formTypeStructure';
import BoolFormField from './ui/BoolFormField.vue';
import CheckboxFormField from './ui/CheckboxFormField.vue';
import DateFormField from './ui/DateFormField.vue';
import DropdownFormField from './ui/DropdownFormField.vue';
import FileFormField from './ui/FileFormField.vue';
import NumberFormField from './ui/NumberFormField.vue';
import PasswordFormField from './ui/PasswordFormField.vue';
import RadioFormField from './ui/RadioFormField.vue';
import TextareaFormField from './ui/TextareaFormField.vue';
import TextFormField from './ui/TextFormField.vue';
import ValidationBtn from './../button/ValidationBtn.vue';
// Champs spéciaux
import { ImageFormField, PDFFormField, VideoFormField, AudioFormField, DocumentFormField, VoiceFormField, TipTapFormField, OTPFormField } from './ui/special';

// Utilisation de lucide-vue-next pour les icônes
import { Edit, Eye, Plus } from 'lucide-vue-next'


interface Props {
  field: FormField;
  name?: string;
  currentLang?: string;
  formValues?: Record<string, any>;
  showControlButtons?: boolean;
  hasFilledData?: boolean;
  onOpenModal?: () => void;
  onOpenEdit?: () => void;
  onOpenView?: () => void;
  className?: string;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  'field-change': [fieldName: string, value: any];
}>();

const fieldKey = computed(() => props.name ?? props.field.fieldName);

// Normaliser defaultValue en fonction du type de champ
const rawDefaultValue = computed(() => props.formValues?.[fieldKey.value]);
const defaultValue = computed(() => {
  const value = rawDefaultValue.value;

  // Pour les champs fichier, on ne veut PAS passer un FileList ou File brut
  if (props.field.fieldType === 'file') {
    if (typeof value === 'string') return value;
    if (Array.isArray(value) && value.every((v) => typeof v === 'string')) {
      return value;
    }
    // Dans tous les autres cas (FileList, File, etc.), on ignore la valeur
    return undefined;
  }

  return value as any;
});

const handleFieldChange = (value: any) => {
  emit('field-change', fieldKey.value, value);
};
</script>

<template>
  <div>
    <!-- Rendu dynamique du champ -->
    <component
      :is="resolveComponent(field.fieldType)"
      :field="field as any"
      :name="fieldKey"
      :current-lang="currentLang"
      :default-value="defaultValue"
      :class="className"
      @update:modelValue="handleFieldChange"
      @change="handleFieldChange"
      @input="handleFieldChange"
    />

    <!-- Boutons d'action -->
    <div
      v-if="showControlButtons"
      class="flex justify-end mb-4 space-x-2"
    >
      <ValidationBtn
        v-if="!hasFilledData"
        type="button"
        :heigth-ratio="0.23"
        title="Remplir"
        @click.prevent.stop="onOpenModal?.()"
      >
        <Plus class="w-5 h-5" />
      </ValidationBtn>

      <template v-else>
        <ValidationBtn
          type="button"
          :heigth-ratio="0.23"
          title="Modifier"
          @click.prevent.stop="onOpenEdit?.()"
        >
          <Edit class="w-5 h-5" />
        </ValidationBtn>

        <ValidationBtn
          type="button"
          :heigth-ratio="0.23"
          title="Voir"
          @click.prevent.stop="onOpenView?.()"
        >
          <Eye class="w-5 h-5" />
        </ValidationBtn>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
function resolveComponent(type: string) {
  switch (type) {
    case 'text':
      return TextFormField;
    case 'textarea':
      return TextareaFormField;
    case 'password':
      return PasswordFormField;
    case 'number':
      return NumberFormField;
    case 'radio':
      return RadioFormField;
    case 'checkbox':
      return CheckboxFormField;
    case 'bool':
      return BoolFormField;
    case 'dropdown':
      return DropdownFormField;
    case 'file':
      return FileFormField;
    case 'date':
      return DateFormField;
    // Champs spéciaux
    case 'IMAGE':
      return ImageFormField;
    case 'PDF':
      return PDFFormField;
    case 'VIDEO':
      return VideoFormField;
    case 'AUDIO':
      return AudioFormField;
    case 'DOCUMENT':
      return DocumentFormField;
    case 'VOICE':
      return VoiceFormField;
    case 'TIP_TAP_DOC_TEXT':
      return TipTapFormField;
    case 'OTP':
      return OTPFormField;
    default:
      return {
        template: `<div class="text-red-500 font-semibold">Champ non supporté : <code>${type}</code></div>`,
      };
  }
}
</script>
